<template>
  <div class="col-12">
    <div class="card bg-white mb-3">
      <div class="card-body">
        <div id="roles-grid">
          <h3 class="mt-2">{{ $t("tenantsGrid.tenants") }}</h3>
          <BButtonToolbar class="mb-2 pt-2 border-light border-top">
            <BButtonGroup>
              <BButton
                variant="outline-primary"
                size="sm"
                @click="onAddNew"
                v-permission="'AbpTenantManagement.Tenants.Create'"
                class="custom-button"
              >
                <font-awesome-icon icon="plus"></font-awesome-icon>
                {{ $t("tenantsGrid.addNew") }}
              </BButton>
              <BButton
                variant="light"
                size="sm"
                @click="onDelete"
                class="ml-2"
                :disabled="datas.selected.length === 0"
                v-permission="'AbpTenantManagement.Tenants.Delete'"
                style="background-color: #e9ecef"
              >
                {{ $t("tenantsGrid.delete") }}
              </BButton>
            </BButtonGroup>
            <div class="rightEnd">
              <div class="search">
                <font-awesome-icon :fixedWidth="true" :icon="['fas', 'magnifying-glass']" class="searchIcon" />
                <input
                  ref="search"
                  type="text"
                  class="searchInput"
                  placeholder="Search..."
                  v-model="searchValue"
                  @input="readData()"
                  id="search"
                  aria-label="Search"
                />
              </div>
              <BPagination
                id="grid-pagination"
                v-model="pagination.page"
                :total-rows="datas.total"
                :per-page="pagination.maxResultCount"
                aria-controls="table"
                @update:modelValue="onPaginationChange"
                class="ml-auto"
              ></BPagination>
            </div>
          </BButtonToolbar>
          <DataTable
            id="table"
            v-model:sortBy="sort.sortBy"
            :busy="isBusy"
            :fields="fields"
            :items="datas.items"
            :no-local-sorting="true"
            v-model:sort-desc.sync="sort.sortDesc"
            fixed
            hover
            select-mode="single"
            selectable
            @row-selected="onRowSelected"
            @sort-changed="onSortChanged"
            :linkFields="{ name: '/saas/tenants/edit/' }"
          >
            <template #table-busy>
              <GridLoading></GridLoading>
            </template>
            <!-- A custom formatted column -->
            <template #cell(name)="data">
              <router-link :to="'/saas/tenants/edit/' + data.item.id">{{ data.item.name }}</router-link>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Inject } from "inversify-props";
import { TenantApi } from "@/api";
import { TenantDto, TenantDtoPagedResultDto } from "@/api/model";
import BaseGridMixin from "@/core/views/grids/BaseGrid";
import { AxiosResponse } from "axios";
import GridLoading from "@/core/views/pages/_components/GridLoading.vue";
import { eventBus } from "@/main";
import { mixins, Options } from "vue-class-component";
import { BButtonToolbar, BButton, BButtonGroup, BPagination } from "bootstrap-vue-next";
import DataTable from "../../_components/Table.vue";

@Options({
  components: { GridLoading, BButtonToolbar, BButton, BButtonGroup, BPagination, DataTable },
  watch: {
    searchValue(newValue: any, oldValue: any) {
      if (newValue != oldValue) {
        this.pagination.page = 1;
      }
    }
  }
})
export default class TenantsGrid extends mixins(BaseGridMixin<TenantDto, TenantDtoPagedResultDto>) {
  @Inject("TenantApi")
  private readonly tenantApi!: TenantApi;
  name = "";
  cdTenantId = "";
  id = "";
  fields: any[] = [];
  searchValue: any = "";
  meta = {
    formRoute: "TenantFormCreate"
  };

  created(): void {
    eventBus.emit("pageName", "Tenants");
    this.name = this.$t("tenantsGrid.name") as string;
    this.cdTenantId = this.$t("tenantsGrid.cdTenantId") as string;
    this.id = this.$t("tenantsGrid.id") as string;
    this.fields = [
      { key: "name", label: this.name, sortable: true },
      { key: "extraProperties.CDTenantId", label: this.cdTenantId, sortable: true },
      {
        key: "id",
        label: this.id,
        sortable: false,
        thStyle: { width: "20%" }
      }
    ];
  }
  getRequest(
    skipCount?: number,
    maxResultCount?: number,
    sorting?: string
  ): Promise<AxiosResponse<TenantDtoPagedResultDto>> {
    let res;
    if (this.searchValue == " ") {
      res = this.tenantApi.apiMultiTenancyTenantsGet(undefined, sorting, skipCount, maxResultCount);
    } else {
      res = this.tenantApi.apiMultiTenancyTenantsGet(this.searchValue.trim(), sorting, skipCount, maxResultCount);
    }
    return res;
  }

  deleteRequest(id: string): Promise<AxiosResponse<void>> {
    return this.tenantApi.apiMultiTenancyTenantsIdDelete(id);
  }
}
</script>
<style scoped>
.custom-button {
  color: #0054a6;
  background-color: white;
  border-color: #0054a6;
  transition: all 0.02s ease;
}

.custom-button:hover {
  color: white !important;
  background-color: #0054a6 !important;
  border-color: #0054a6;
}
#roles-grid {
  font-size: 14px;
  font-family: Arial, Sans-serif;
}
.rightEnd {
  display: flex;
  align-items: flex-start;
  margin-left: auto;
  gap: 1rem;
}
.search {
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 3px;
}

.searchIcon {
  font-size: 16px;
  padding: 8.5px;
  border: 1px solid rgba(0, 0, 0, 0.304);
  border-right: none;
  border-radius: 5px 0 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.searchInput {
  border: 1px solid rgba(0, 0, 0, 0.304);
  border-left: none;
  border-radius: 0 5px 5px 0;
  padding: 6px 10px;
  outline: none;
  font-size: 14px;
  width: 300px;
}

.searchInput:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}
</style>
